import React, { useEffect, useState } from 'react';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, ExternalLink, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';

const STATUS_META = {
  Active: { color: 'hsl(var(--success))', dot: 'bg-[hsl(var(--success))]', label: 'Active', Icon: ShieldCheck },
  Valid: { color: 'hsl(var(--success))', dot: 'bg-[hsl(var(--success))]', label: 'Active', Icon: ShieldCheck },
  Syncing: { color: 'hsl(var(--info))', dot: 'bg-[hsl(var(--info))] animate-pulse', label: 'Syncing', Icon: RefreshCw },
  Invalid: { color: 'hsl(var(--danger))', dot: 'bg-[hsl(var(--danger))]', label: 'Invalid', Icon: ShieldAlert },
};

export function FmcsaWidget({ readOnly = false }) {
  const [f, setF] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => client.get('/fmcsa').then((r) => setF(r.data)).catch(() => setF(false));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    setF((p) => (p ? { ...p, status: 'Syncing' } : p));
    try {
      const r = await client.post('/fmcsa/sync');
      setF((p) => ({ ...(p || {}), ...r.data }));
      toast.info(r.data.message || 'FMCSA credentials re-verified');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); load(); }
    finally { setSyncing(false); }
  };

  if (f === null) return <Card className="h-full p-5"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading FMCSA status…</div></Card>;
  if (f === false) return null;

  const meta = STATUS_META[syncing ? 'Syncing' : f.status] || STATUS_META.Invalid;
  const Icon = meta.Icon;
  const portal = f.portal_url || 'https://portal.fmcsa.dot.gov/';

  return (
    <Card className="flex h-full flex-col p-5" data-testid="fmcsa-widget">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `color-mix(in srgb, ${meta.color} 14%, transparent)` }}>
            <Icon className="h-5 w-5" style={{ color: meta.color }} />
          </div>
          <div>
            <div className="font-display text-base font-semibold">FMCSA Credentials</div>
            <div className="text-[11px] text-muted-foreground">DOT #{f.dot_number || '—'}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full border px-2.5 py-1" data-testid="fmcsa-status-badge">
          <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
          <span className="text-xs font-semibold" style={{ color: meta.color }}>{syncing ? 'Syncing' : meta.label}</span>
        </div>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="fmcsa-last-confirmed">
        Last confirmed: <span className="font-medium text-foreground">{f.last_confirmed ? new Date(f.last_confirmed).toLocaleString() : '—'}</span>
      </div>
      {!f.live_api && <div className="mt-1 text-[11px] text-muted-foreground">Live sync connects to the FMCSA Portal.</div>}

      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="fmcsa-sync-button">
            {syncing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}Sync
          </Button>
        )}
        <Button size="sm" onClick={() => window.open(portal, '_blank', 'noopener')} data-testid="fmcsa-portal-link">
          <ExternalLink className="mr-1.5 h-3.5 w-3.5" />FMCSA Portal
        </Button>
      </div>
    </Card>
  );
}
